/**
 * Extra solid (filled) glyphs — the companion to ./solid-icons for every
 * concept that file doesn't cover. 24×24, `currentColor`, default size 24.
 *
 * Holes are even-odd or masked (never a hard-coded knock-out colour), so each
 * glyph works on any background. A handful of icons whose whole meaning is a
 * line (trend, pulse, link, shuffle…) are drawn as a heavy 2.2–3px stroke,
 * matching the `Ui*` glyphs in ./solid-icons.
 */
import { useId, type FC, type JSX, type ReactNode, type SVGProps } from "react";

export type SolidIconProps = { size?: number } & Omit<SVGProps<SVGSVGElement>, "fill">;
export type SolidIcon = FC<SolidIconProps>;
/** Concrete glyph type — structurally identical to the glyphs in ./solid-icons. */
export type SolidGlyph = ((p: SolidIconProps) => JSX.Element) & { displayName?: string };

function Base({ size = 24, children, ...rest }: SolidIconProps & { children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false" {...rest}>
      {children}
    </svg>
  );
}

/** Filled even-odd path. */
const P = (d: string) => <path d={d} fillRule="evenodd" clipRule="evenodd" />;
/** Heavy stroke path. */
const L = (d: string, w = 2.6) => (
  <path d={d} fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
);
const Dot = (cx: number, cy: number, r: number) => <circle cx={cx} cy={cy} r={r} />;

function glyph(name: string, body: ReactNode): SolidGlyph {
  const Icon = (p: SolidIconProps) => <Base {...p}>{body}</Base>;
  Icon.displayName = name;
  return Icon;
}

/**
 * Filled shape with stroked (and optionally filled) knock-outs cut through it
 * via a mask, plus optional overlay drawn on top.
 */
function knock(name: string, fillD: string, cut: ReactNode, overlay?: ReactNode): SolidGlyph {
  const Icon = (p: SolidIconProps) => {
    const id = `sx${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
    return (
      <Base {...p}>
        <defs>
          <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <rect width="24" height="24" fill="#fff" />
            <g fill="none" stroke="#000" strokeLinecap="round" strokeLinejoin="round">
              {cut}
            </g>
          </mask>
        </defs>
        <path d={fillD} fillRule="evenodd" clipRule="evenodd" mask={`url(#${id})`} />
        {overlay}
      </Base>
    );
  };
  Icon.displayName = name;
  return Icon;
}
const C = (d: string, w = 2.2) => <path d={d} strokeWidth={w} />;
const CFill = (d: string) => <path d={d} fill="#000" stroke="none" />;
/** Slash for the "*Off" variants: a wide cut plus a narrower drawn line. */
const SLASH = "M3 3l18 18";
const slashCut = C(SLASH, 4.4);
const slashLine = L(SLASH, 2);

// ── Shared outlines ──────────────────────────────────────────────────────
const CIRCLE = "M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20z";
const RSQUARE = "M6 3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3z";
const TRIANGLE = "M10.3 3.3a2 2 0 0 1 3.4 0l8.4 14.6A2 2 0 0 1 20.4 21H3.6a2 2 0 0 1-1.7-3.1z";
const SHIELD = "M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5z";
const FILE = "M6 2h8.5L20 7.5V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z";
const CALENDAR = "M7 2h2v2h6V2h2v2h2a2.5 2.5 0 0 1 2.5 2.5V19a2.5 2.5 0 0 1-2.5 2.5H5A2.5 2.5 0 0 1 2.5 19V6.5A2.5 2.5 0 0 1 5 4h2z";
const CLOUD = "M7 19a5 5 0 0 1-.9-9.9A6.5 6.5 0 0 1 18.4 9 4.5 4.5 0 0 1 17.5 19z";
const CLOUD_HIGH = "M7 15a4.5 4.5 0 0 1-.8-8.9 6 6 0 0 1 11.3-1 4 4 0 0 1-.5 9.9z";
const EYE =
  "M12 5c5 0 8.6 3.3 10 7-1.4 3.7-5 7-10 7S3.4 15.7 2 12c1.4-3.7 5-7 10-7zm0 3.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6zm0 2a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6z";
const WIFI = "M12 20.5 1.2 8.4a16.2 16.2 0 0 1 21.6 0z";
const PUSHPIN = "M8 2h8v2h-1v5.2l3 3V15h-5v5.5L12 22l-1-1.5V15H6v-2.8l3-3V4H8z";
const HEART = "M12 21s-9-5.3-9-11.8A5 5 0 0 1 12 6.4a5 5 0 0 1 9 2.8C21 15.7 12 21 12 21z";
const IMAGE = "M4 3h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm4.5 3.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM4.5 18.5h15l-4.8-7-3.7 5-2.2-2.4z";
const LENS = "M10.5 2.5a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10z";
const CLIPBOARD = "M9 2h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2V3a1 1 0 0 1 1-1z";
const PERSON = "M9 3.9a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2zM2.5 19.2c0-3.7 2.9-6.2 6.5-6.2s6.5 2.5 6.5 6.2v.8h-13z";

function rosette(points: number, outer: number, inner: number): string {
  const pts: string[] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / points - Math.PI / 2;
    pts.push(`${(12 + r * Math.cos(a)).toFixed(2)} ${(12 + r * Math.sin(a)).toFixed(2)}`);
  }
  return `M${pts.join("L")}z`;
}

// ── Marks & disclosure ───────────────────────────────────────────────────
export const XFill = glyph("XFill", L("M6 6l12 12M18 6 6 18", 3));
export const CheckFill = glyph("CheckFill", L("m4.5 12.5 5 5L19.5 7", 3));
export const CheckCheckFill = glyph("CheckCheckFill", L("M1.8 12.5 6 16.8l9-9.3M11.3 15.8l1 1L22 7", 2.6));
export const PlusFill = glyph("PlusFill", L("M12 4.5v15M4.5 12h15", 3));
export const MinusFill = glyph("MinusFill", L("M5 12h14", 3));
export const ChevronDownFill = glyph("ChevronDownFill", L("m6 9 6 6 6-6", 3));
export const ChevronUpFill = glyph("ChevronUpFill", L("m6 15 6-6 6 6", 3));
export const ChevronLeftFill = glyph("ChevronLeftFill", L("m15 6-6 6 6 6", 3));
export const ChevronRightFill = glyph("ChevronRightFill", L("m9 6 6 6-6 6", 3));
export const HashFill = glyph("HashFill", L("M9.5 4 8 20M16 4l-1.5 16M4.5 9h16M3.5 15h16", 2.6));
export const MenuFill = glyph("MenuFill", P("M3 4.8h18v2.8H3zm0 5.8h18v2.8H3zm0 5.8h18v2.8H3z"));
export const ListFill = glyph("ListFill", P("M3 5h2.8v2.8H3zm0 5.6h2.8v2.8H3zm0 5.6h2.8V19H3zM8 5h13v2.8H8zm0 5.6h13v2.8H8zm0 5.6h13V19H8z"));
export const GripVerticalFill = glyph(
  "GripVerticalFill",
  <>{Dot(9, 5, 1.8)}{Dot(15, 5, 1.8)}{Dot(9, 12, 1.8)}{Dot(15, 12, 1.8)}{Dot(9, 19, 1.8)}{Dot(15, 19, 1.8)}</>,
);

// ── Arrows ───────────────────────────────────────────────────────────────
export const ArrowRightFill = glyph("ArrowRightFill", P("M13 4.5 20.5 12 13 19.5v-5H3.5v-5H13z"));
export const ArrowLeftFill = glyph("ArrowLeftFill", P("M11 4.5 3.5 12l7.5 7.5v-5h9.5v-5H11z"));
export const ArrowUpRightFill = glyph("ArrowUpRightFill", P("M8 4.5h11.5V16l-4.1-4.1-7.1 7.1-3.3-3.3 7.1-7.1z"));
export const ArrowDownRightFill = glyph("ArrowDownRightFill", P("M8 19.5h11.5V8l-4.1 4.1-7.1-7.1-3.3 3.3 7.1 7.1z"));
export const ArrowUpCircleFill = knock("ArrowUpCircleFill", CIRCLE, C("M12 16.5v-9M8 11l4-4 4 4", 2.4));
export const ArrowRightCircleFill = knock("ArrowRightCircleFill", CIRCLE, C("M7.5 12h9M13 8l4 4-4 4", 2.4));
export const DownloadFill = glyph("DownloadFill", P("M10.4 2.5h3.2v8.3h4.1L12 17l-5.7-6.2h4.1zM3.5 18.8h17v2.7h-17z"));
export const UploadFill = glyph("UploadFill", P("M12 2.5l5.7 6.2h-4.1V17h-3.2V8.7H6.3zM3.5 18.8h17v2.7h-17z"));
export const RefreshFill = glyph("RefreshFill", <>{L("M19.5 12a7.5 7.5 0 0 1-13.3 4.8M4.5 12a7.5 7.5 0 0 1 13.3-4.8", 2.6)}{P("M21 3v7h-7zM3 21v-7h7z")}</>);
export const RotateCwFill = glyph("RotateCwFill", <>{L("M19.5 12a7.5 7.5 0 1 1-2.2-5.3", 2.6)}{P("M21 3v7.2h-7.2z")}</>);
export const RotateCcwFill = glyph("RotateCcwFill", <>{L("M4.5 12a7.5 7.5 0 1 0 2.2-5.3", 2.6)}{P("M3 3v7.2h7.2z")}</>);
export const ExternalLinkFill = glyph(
  "ExternalLinkFill",
  <>{L("M10.5 4.5H6.5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-4", 2.6)}{P("M13.5 2.5h8v8l-2.9-2.9-5.6 5.6-2.2-2.2 5.6-5.6z")}</>,
);
export const MoveFill = glyph("MoveFill", <>{L("M12 5v14M5 12h14", 2.4)}{P("M12 1.5l3.5 4h-7zM12 22.5l-3.5-4h7zM1.5 12l4-3.5v7zM22.5 12l-4 3.5v-7z")}</>);
export const ShuffleFill = glyph(
  "ShuffleFill",
  <>{L("M3 17h3c2.2 0 3.3-1.2 4.3-3.2l1.9-3.8c1-2 2.1-3 4.3-3H19M3 7h3c1.3 0 2.2.4 2.9 1.2M19 17h-2.5c-1.3 0-2.2-.4-2.9-1.2", 2.4)}{P("M18 3.5l4 3.5-4 3.5zM18 13.5l4 3.5-4 3.5z")}</>,
);
export const TrendingUpFill = glyph("TrendingUpFill", <>{L("M3 17.5l6-6 4 4 6.5-6.5", 2.8)}{P("M14.5 5.5H22V13z")}</>);
export const TrendingDownFill = glyph("TrendingDownFill", <>{L("M3 6.5l6 6 4-4 6.5 6.5", 2.8)}{P("M14.5 18.5H22V11z")}</>);
export const ActivityFill = glyph("ActivityFill", L("M2.5 12h4l3-7.5 5 15 3-7.5h4", 2.6));

// ── Status circles ───────────────────────────────────────────────────────
export const CircleFill = glyph("CircleFill", P("M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm0 3a7 7 0 1 0 0 14 7 7 0 0 0 0-14z"));
export const SquareFill = glyph("SquareFill", P(`${RSQUARE}M5.8 5.8v12.4h12.4V5.8z`));
export const CheckCircleFill = knock("CheckCircleFill", CIRCLE, C("m7.5 12.3 3 3 6-6.3", 2.5));
export const CheckSquareFill = knock("CheckSquareFill", RSQUARE, C("m7.5 12.3 3 3 6-6.3", 2.5));
export const BadgeCheckFill = knock("BadgeCheckFill", rosette(12, 10.5, 8.6), C("m8 12.3 2.8 2.8 5.4-5.8", 2.4));
export const XCircleFill = knock("XCircleFill", CIRCLE, C("m8.5 8.5 7 7m0-7-7 7", 2.5));
export const PlusCircleFill = knock("PlusCircleFill", CIRCLE, C("M12 7.5v9M7.5 12h9", 2.5));
export const AlertCircleFill = knock("AlertCircleFill", CIRCLE, C("M12 7v6.2M12 16.8v.1", 2.5));
export const InfoFill = knock("InfoFill", CIRCLE, C("M12 11v6M12 7.2v.1", 2.5));
export const HelpCircleFill = knock("HelpCircleFill", CIRCLE, C("M9.3 9.4a2.8 2.8 0 1 1 3.9 2.5c-.8.4-1.2.9-1.2 1.8M12 17v.1", 2.3));
export const AlertTriangleFill = knock("AlertTriangleFill", TRIANGLE, C("M12 9v4.8M12 17.4v.1", 2.3));
export const ClockFill = knock("ClockFill", CIRCLE, C("M12 6.5V12l3.5 2.2", 2.3));
export const TimerFill = knock("TimerFill", "M9 1.5h6v2.2H9zM12 4.8a8.6 8.6 0 1 1 0 17.2 8.6 8.6 0 0 1 0-17.2z", C("M12 9.5v4.3", 2.3));
export const TargetFill = glyph(
  "TargetFill",
  P("M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm0 3a7 7 0 1 0 0 14 7 7 0 0 0 0-14zm0 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"),
);
export const CompassFill = glyph("CompassFill", P(`${CIRCLE}M16.3 7.7l-2.5 6.1-6.1 2.5 2.5-6.1z`));
export const GaugeFill = knock("GaugeFill", "M12 3a10 10 0 0 1 10 10c0 2.4-.8 4.6-2.3 6.3H4.3A9.9 9.9 0 0 1 2 13 10 10 0 0 1 12 3z", <>{C("M12 14.5l4-5", 2.3)}{C("M12 14.5v.1", 4)}</>);
export const LoaderFill: SolidGlyph = ({ className, ...p }: SolidIconProps) => (
  <Base className={["ch-icon-spin", className ?? ""].filter(Boolean).join(" ")} {...p}>
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
      const a = (Math.PI / 4) * i - Math.PI / 2;
      return <circle key={i} cx={12 + 8 * Math.cos(a)} cy={12 + 8 * Math.sin(a)} r={2.3 - i * 0.15} opacity={1 - i * 0.1} />;
    })}
  </Base>
);
LoaderFill.displayName = "LoaderFill";

// ── People ───────────────────────────────────────────────────────────────
export const UserCircleFill = glyph(
  "UserCircleFill",
  P(`${CIRCLE}M12 5.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7zM5.8 17.6c1.3-1.9 3.6-3.1 6.2-3.1s4.9 1.2 6.2 3.1A8 8 0 0 1 12 20a8 8 0 0 1-6.2-2.4z`),
);
export const UserXFill = glyph("UserXFill", <>{P(PERSON)}{L("m16.8 8 4.7 4.7m0-4.7-4.7 4.7", 2.4)}</>);
export const AccessibilityFill = glyph("AccessibilityFill", <>{Dot(12, 4.2, 2.2)}{L("M4.5 8.5l7.5 1.6 7.5-1.6M12 10.1v5M12 15.1l-3.6 6M12 15.1l3.6 6", 2.4)}</>);
export const SmileFill = glyph(
  "SmileFill",
  P(`${CIRCLE}M8.5 8a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm7 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM7.3 13.5a4.7 4.7 0 0 0 9.4 0z`),
);
export const EarFill = glyph(
  "EarFill",
  P("M12.5 2a7 7 0 0 1 7 7c0 2.6-1.2 4-2.3 5.2-.9 1-1.2 1.7-1.2 3.3a4.5 4.5 0 0 1-8.3 2.4l1.8-1a2.4 2.4 0 0 0 4.4-1.4c0-2.4.8-3.6 1.8-4.7.9-1 1.6-1.9 1.6-3.8a4.8 4.8 0 0 0-9.6 0H5.5a7 7 0 0 1 7-7zm0 4a3 3 0 0 1 3 3h-2a1 1 0 0 0-2 0c0 1 .9 1.4.9 2.7h-2c0-1.2-.9-1.8-.9-2.7a3 3 0 0 1 3-3z"),
);
export const ScanFaceFill = glyph(
  "ScanFaceFill",
  <>
    {L("M3 8V5a2 2 0 0 1 2-2h3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3", 2.4)}
    {P("M12 6.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm-2 3a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zm4 0a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zm-4.2 3.8a2.9 2.9 0 0 0 4.4 0z")}
  </>,
);
export const FingerprintFill = glyph(
  "FingerprintFill",
  L("M4.5 15a13 13 0 0 0 .5-3.5 7 7 0 0 1 12.6-4.2M19 11.5c0 3.5-.4 6.5-1.3 9M8 19.5a14 14 0 0 0 1-8 3 3 0 0 1 6 0c0 3.4-.5 6.4-1.6 9M12 11.5c0 3.8-.8 7-2.4 9.7", 2.1),
);

// ── Documents & files ────────────────────────────────────────────────────
export const FileXFill = knock("FileXFill", FILE, C("m9.5 11.5 5 5m0-5-5 5", 2.3));
export const FileWarningFill = knock("FileWarningFill", FILE, C("M12 10v4.5M12 17.6v.1", 2.3));
export const FileSpreadsheetFill = knock("FileSpreadsheetFill", FILE, C("M7.5 11h9v7.5h-9zM12 11v7.5M7.5 14.75h9", 1.5));
export const FileSignatureFill = knock("FileSignatureFill", FILE, C("M7.5 17c1.4-2.2 2.4-2.2 3 0s1.5 1.5 2.5 0 1.8-1 3 .4M7.5 11.5h6", 1.7));
export const TrashFill = glyph(
  "TrashFill",
  P("M9 2h6a1 1 0 0 1 1 1v1h4.5v2.2h-17V4H8V3a1 1 0 0 1 1-1zM5 7.5h14l-1 12.7A2 2 0 0 1 16 22H8a2 2 0 0 1-2-1.8zm4 3v8h1.8v-8zm4.2 0v8H15v-8z"),
);
export const ScrollTextFill = glyph("ScrollTextFill", P("M5 2h11l4 4v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm2 6v1.6h9V8zm0 4v1.6h9V12zm0 4v1.6h6V16z"));
export const ClipboardCheckFill = knock("ClipboardCheckFill", CLIPBOARD, C("m8.5 13.5 2.5 2.5 4.5-5", 2.3));
export const ReceiptFill = glyph("ReceiptFill", P("M5 2h14v20l-2.3-1.5L14.3 22 12 20.5 9.7 22l-2.4-1.5L5 22zm3 5v1.8h8V7zm0 4v1.8h8V11zm0 4v1.8h5V15z"));
/**
 * Scissors — the cut-here mark. Two crossing strokes and two open rings rather
 * than a filled silhouette: the whole meaning of this glyph is the crossing,
 * and a solid pair of scissors at 16px is an unreadable blob.
 */
export const ScissorsFill = glyph(
  "ScissorsFill",
  <>
    {L("M7.6 7.2 20 19.6", 2.2)}
    {L("M7.6 16.8 20 4.4", 2.2)}
    <circle cx={5.4} cy={5.4} r={2.5} fill="none" stroke="currentColor" strokeWidth={2.2} />
    <circle cx={5.4} cy={18.6} r={2.5} fill="none" stroke="currentColor" strokeWidth={2.2} />
  </>,
);
export const CopyFill = glyph("CopyFill", P("M9 7h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM5 3h10v2.2H5.2v9.8H3V5a2 2 0 0 1 2-2z"));
export const SaveFill = glyph("SaveFill", P("M4 3h12.5L21 7.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm3 2v4h8V5zm5 8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"));
export const ArchiveFill = glyph("ArchiveFill", P("M2 3h20v5H2zM3.5 9.5h17V19a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2zM9 12v1.8h6V12z"));
export const InboxFill = glyph(
  "InboxFill",
  P("M5.5 3h13a1.5 1.5 0 0 1 1.4 1l2.1 7.5V19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-7.5L4.1 4a1.5 1.5 0 0 1 1.4-1zM4.6 11h4.4a3 3 0 0 0 6 0h4.4L17.6 5H6.4z"),
);
export const NewspaperFill = glyph(
  "NewspaperFill",
  P("M4 3h13a1 1 0 0 1 1 1v15a2 2 0 0 0 2 2H5a2 2 0 0 1-2-2V4a1 1 0 0 1 1-1zm2 4v4h5V7zm7 0v1.6h3V7zm0 3v1.6h3V10zm-7 4v1.6h10V14zm0 3v1.6h10V17zM19.5 8H21a1 1 0 0 1 1 1v10a2 2 0 0 1-2.5 1.9z"),
);
export const BookMarkedFill = glyph("BookMarkedFill", P("M7 2h13v16H7a1.5 1.5 0 0 0 0 3h13v1H7a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3zm4 0v7l2-1.5L15 9V2z"));
export const TicketFill = glyph(
  "TicketFill",
  P("M4.5 5h15A2.5 2.5 0 0 1 22 7.5v2.2a2.4 2.4 0 0 0 0 4.6v2.2a2.5 2.5 0 0 1-2.5 2.5h-15A2.5 2.5 0 0 1 2 16.5v-2.2a2.4 2.4 0 0 0 0-4.6V7.5A2.5 2.5 0 0 1 4.5 5zM14.2 7v2h1.6V7zm0 4v2h1.6v-2zm0 4v2h1.6v-2z"),
);
export const TagFill = glyph("TagFill", P("M3 3h8.2a2 2 0 0 1 1.4.6l8.2 8.2a2 2 0 0 1 0 2.8l-6.2 6.2a2 2 0 0 1-2.8 0L3.6 12.6A2 2 0 0 1 3 11.2zm4.5 2.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"));
export const PaperclipFill = glyph("PaperclipFill", L("M20 11.5l-8.2 8.2a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.6 1.6 0 0 1-2.3-2.3l7.8-7.8", 2.3));
export const LinkFill = glyph("LinkFill", L("M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.2 1.2M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.2-1.2", 2.5));
export const ImageFill = glyph("ImageFill", P(IMAGE));
export const ImagePlusFill = knock("ImagePlusFill", IMAGE, CFill("M18.5-.5a6 6 0 1 1 0 12 6 6 0 0 1 0-12z"), L("M18.5 2v7M15 5.5h7", 2.4));
export const FilmFill = glyph(
  "FilmFill",
  P("M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v2h2V5zm12 0v2h2V5zM5 9v2h2V9zm12 0v2h2V9zM5 13v2h2v-2zm12 0v2h2v-2zM5 17v2h2v-2zm12 0v2h2v-2z"),
);
export const QrCodeFill = glyph(
  "QrCodeFill",
  P("M3 3h8v8H3zm2.2 2.2v3.6h3.6V5.2zM13 3h8v8h-8zm2.2 2.2v3.6h3.6V5.2zM3 13h8v8H3zm2.2 2.2v3.6h3.6v-3.6zM13 13h3v3h-3zm5 0h3v3h-3zm-2 3h2v2h-2zm-3 2h3v3h-3zm5 0h3v3h-3zM6.2 6.2h1.6v1.6H6.2zm10 0h1.6v1.6h-1.6zm-10 10h1.6v1.6H6.2z"),
);

// ── Calendar & time ──────────────────────────────────────────────────────
export const CalendarRangeFill = glyph("CalendarRangeFill", P(`${CALENDAR}M5.5 11v2.2h9V11zm4 4.5v2.2h9v-2.2z`));
export const CalendarOffFill = knock("CalendarOffFill", CALENDAR, slashCut, slashLine);
export const CalendarClockFill = knock(
  "CalendarClockFill",
  CALENDAR,
  CFill("M17.5 10.5a7 7 0 1 1 0 14 7 7 0 0 1 0-14z"),
  <>{P("M17.5 12.3a5.2 5.2 0 1 1 0 10.4 5.2 5.2 0 0 1 0-10.4zm-.9 2.2v3.4l2.7 1.7.9-1.4-1.9-1.2v-2.5z")}</>,
);

// ── Devices & tech ───────────────────────────────────────────────────────
export const MonitorFill = glyph("MonitorFill", P("M4 3h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-6v2h3v2H7v-2h3v-2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"));
export const LaptopFill = glyph("LaptopFill", P("M5 4h14a2 2 0 0 1 2 2v9H3V6a2 2 0 0 1 2-2zM1 16.5h22v1a2.5 2.5 0 0 1-2.5 2.5h-17A2.5 2.5 0 0 1 1 17.5z"));
export const SmartphoneFill = glyph("SmartphoneFill", P("M7.5 1.5h9A2.5 2.5 0 0 1 19 4v16a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 5 20V4a2.5 2.5 0 0 1 2.5-2.5zM10 18.5v1.6h4v-1.6z"));
export const TabletFill = glyph("TabletFill", P("M6 2h12a2.5 2.5 0 0 1 2.5 2.5v15A2.5 2.5 0 0 1 18 22H6a2.5 2.5 0 0 1-2.5-2.5v-15A2.5 2.5 0 0 1 6 2zm4.5 16v1.6h3V18z"));
export const PhoneFill = glyph(
  "PhoneFill",
  P("M20 15.4c-1.2 0-2.4-.2-3.5-.6a1 1 0 0 0-1 .3l-1.6 2c-2.8-1.4-5.5-3.9-6.9-6.8l2-1.7c.3-.3.3-.7.2-1-.4-1.1-.6-2.3-.6-3.5 0-.6-.4-1-1-1H4.2C3.6 3 3 3.2 3 4c0 9.3 7.7 17 17 17 .7 0 1-.6 1-1.2v-3.4c0-.6-.4-1-1-1z"),
);
export const CpuFill = glyph(
  "CpuFill",
  <>{P("M7 4h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3zm2.5 5.5v5h5v-5z")}{L("M9 1.8V4M15 1.8V4M9 20v2.2M15 20v2.2M1.8 9H4M1.8 15H4M20 9h2.2M20 15h2.2", 2)}</>,
);
export const DatabaseFill = glyph(
  "DatabaseFill",
  P("M12 2c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zM4 7.6C5.6 8.8 8.6 9.5 12 9.5s6.4-.7 8-1.9V11c0 1.7-3.6 3-8 3s-8-1.3-8-3zm0 6c1.6 1.2 4.6 1.9 8 1.9s6.4-.7 8-1.9V19c0 1.7-3.6 3-8 3s-8-1.3-8-3z"),
);
export const WifiFill = glyph("WifiFill", P(WIFI));
export const WifiOffFill = knock("WifiOffFill", WIFI, slashCut, slashLine);
export const RadioFill = glyph(
  "RadioFill",
  <>{Dot(12, 12, 2.6)}{L("M8.2 8.2a5.4 5.4 0 0 0 0 7.6M15.8 8.2a5.4 5.4 0 0 1 0 7.6M5.1 5.1a9.8 9.8 0 0 0 0 13.8M18.9 5.1a9.8 9.8 0 0 1 0 13.8", 2.3)}</>,
);
export const PrinterFill = glyph("PrinterFill", P("M6 2h12v5H6zM4 8h16a2 2 0 0 1 2 2v7h-4v-3H6v3H2v-7a2 2 0 0 1 2-2zm13 2.5v1.6h2v-1.6zM7.5 15.5h9V22h-9z"));
export const CameraFill = glyph(
  "CameraFill",
  P("M9 3.5h6l1.7 2.5H20a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3.3zM12 8.5a4.2 4.2 0 1 0 0 8.4 4.2 4.2 0 0 0 0-8.4zm0 2.2a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"),
);
export const VideoFill = glyph("VideoFill", P("M4 5.5h10a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2zm13.5 4.3 4.5-3v10.4l-4.5-3z"));
export const MicFill = glyph("MicFill", <>{P("M12 2a3.5 3.5 0 0 1 3.5 3.5v6a3.5 3.5 0 0 1-7 0v-6A3.5 3.5 0 0 1 12 2z")}{L("M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M8.5 21h7", 2.2)}</>);
export const MusicFill = glyph("MusicFill", P("M9 5.5 20 3v12.5a3 3 0 1 1-2-2.8V7.6l-7 1.6v8.3a3 3 0 1 1-2-2.8z"));
export const BatteryFill = glyph("BatteryFill", P("M4 6h14a2 2 0 0 1 2 2v1h1.5v6H20v1a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"));
export const PlugFill = glyph("PlugFill", P("M8 2h2v5h4V2h2v5h2v4a6 6 0 0 1-5 5.9V22h-2v-5.1A6 6 0 0 1 6 11V7h2z"));
export const CommandFill = glyph("CommandFill", L("M15 6a3 3 0 1 1 3 3H6a3 3 0 1 1 3-3v12a3 3 0 1 1-3-3h12a3 3 0 1 1-3 3z", 2.4));
export const DeleteFill = knock("DeleteFill", "M8 4h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H8l-6.5-8z", C("m11 9 6 6m0-6-6 6", 2.3));
export const LayoutDashboardFill = glyph("LayoutDashboardFill", P("M3 3h8v10H3zm10 0h8v6h-8zM3 15h8v6H3zm10-4h8v10h-8z"));
export const LayoutFill = glyph("LayoutFill", P("M3 3h18v5H3zm0 7h6v11H3zm8 0h10v11H11z"));
export const LockFill = glyph(
  "LockFill",
  P("M12 2a5 5 0 0 1 5 5v3h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1V7a5 5 0 0 1 5-5zm0 2.2A2.8 2.8 0 0 0 9.2 7v3h5.6V7A2.8 2.8 0 0 0 12 4.2zM11 14v4h2v-4z"),
);
export const LogOutFill = glyph("LogOutFill", P("M4 3h8a1 1 0 0 1 1 1v3h-2.2V5.2H5.2v13.6h5.6V17H13v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm13 4.5 4.5 4.5-4.5 4.5v-3.3H9v-2.4h8z"));
export const LogInFill = glyph("LogInFill", P("M12 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1v-3h2.2v1.8h5.6V5.2h-5.6V7H11V4a1 1 0 0 1 1-1zm-2 4.5 4.5 4.5-4.5 4.5v-3.3H2.5v-2.4H10z"));

// ── Search & view ────────────────────────────────────────────────────────
export const SearchFill = glyph("SearchFill", <>{P(LENS)}{L("M16.4 16.4 21 21", 3.2)}</>);
export const ZoomInFill = glyph("ZoomInFill", <>{P(LENS)}{L("M16.4 16.4 21 21", 3.2)}{L("M10.5 8.3v4.4M8.3 10.5h4.4", 2)}</>);
export const ZoomOutFill = glyph("ZoomOutFill", <>{P(LENS)}{L("M16.4 16.4 21 21", 3.2)}{L("M8.3 10.5h4.4", 2)}</>);
export const EyeOffFill = knock("EyeOffFill", EYE, slashCut, slashLine);

// ── Communication ────────────────────────────────────────────────────────
export const MessageCircleFill = glyph("MessageCircleFill", P("M12 3c5 0 9 3.6 9 8.2s-4 8.2-9 8.2c-1.2 0-2.3-.2-3.3-.5L3.5 21l1.4-4.3A7.8 7.8 0 0 1 3 11.2C3 6.6 7 3 12 3z"));
export const MessagesFill = glyph(
  "MessagesFill",
  P("M3 3h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H8l-4 3v-3H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM19 7h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-1v3l-4-3h-5a2 2 0 0 1-2-2v-.5h7a4 4 0 0 0 4-4z"),
);
export const SendFill = knock("SendFill", "M21.7 2.3 2.8 9.6c-.8.3-.8 1.4 0 1.7l6.9 2.9 2.9 6.9c.3.8 1.4.8 1.7 0z", C("M10.4 13.6 20.6 3.4", 1.6));
export const ShareFill = glyph("ShareFill", <>{Dot(18, 5, 3)}{Dot(6, 12, 3)}{Dot(18, 19, 3)}{L("M8.6 10.6l6.8-4M8.6 13.4l6.8 4", 2.2)}</>);
export const BellFill = glyph("BellFill", P("M12 2a1.5 1.5 0 0 1 1.5 1.5v.7a6.5 6.5 0 0 1 5 6.3v4l2 2.5v1.5h-17V17l2-2.5v-4a6.5 6.5 0 0 1 5-6.3v-.7A1.5 1.5 0 0 1 12 2zM9.5 20h5a2.5 2.5 0 0 1-5 0z"));
export const BellRingFill = glyph(
  "BellRingFill",
  <>{P("M12 2a1.5 1.5 0 0 1 1.5 1.5v.7a6.5 6.5 0 0 1 5 6.3v4l2 2.5v1.5h-17V17l2-2.5v-4a6.5 6.5 0 0 1 5-6.3v-.7A1.5 1.5 0 0 1 12 2zM9.5 20h5a2.5 2.5 0 0 1-5 0z")}{L("M2.5 8.5A9.5 9.5 0 0 1 5 3.5M21.5 8.5A9.5 9.5 0 0 0 19 3.5", 2)}</>,
);
export const GlobeFill = knock("GlobeFill", CIRCLE, C("M2 12h20M12 2c2.6 2.7 4 6.2 4 10s-1.4 7.3-4 10M12 2c-2.6 2.7-4 6.2-4 10s1.4 7.3 4 10", 1.6));

// ── Shields ──────────────────────────────────────────────────────────────
export const ShieldFill = glyph("ShieldFill", P(SHIELD));
export const ShieldAlertFill = knock("ShieldAlertFill", SHIELD, C("M12 7.5v5M12 15.8v.1", 2.4));
export const ShieldXFill = knock("ShieldXFill", SHIELD, C("m9.5 9.5 5 5m0-5-5 5", 2.4));
export const ShieldOffFill = knock("ShieldOffFill", SHIELD, slashCut, slashLine);

// ── Places, travel, objects ──────────────────────────────────────────────
export const MapPinFill = glyph("MapPinFill", P("M12 2a7.5 7.5 0 0 1 7.5 7.5c0 5.4-7.5 12.5-7.5 12.5S4.5 14.9 4.5 9.5A7.5 7.5 0 0 1 12 2zm0 4.7a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6z"));
export const PushPinFill = glyph("PushPinFill", P(PUSHPIN));
export const PinOffFill = knock("PinOffFill", PUSHPIN, slashCut, slashLine);
export const TruckFill = glyph(
  "TruckFill",
  <>{P("M2 5h12a1 1 0 0 1 1 1v2h3.6a1 1 0 0 1 .8.4l2.4 3.2a1 1 0 0 1 .2.6V17a1 1 0 0 1-1 1h-1.2a2.8 2.8 0 0 0-5.6 0H9.8a2.8 2.8 0 0 0-5.6 0H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm13 5v3h4.3l-2.2-3z")}{Dot(7, 18.3, 2)}{Dot(17, 18.3, 2)}</>,
);
export const FuelFill = glyph("FuelFill", <>{P("M4 3h9a1 1 0 0 1 1 1v17H3V4a1 1 0 0 1 1-1zm1.5 2.5v4.5h6V5.5z")}{L("M14 10h1.5a1.5 1.5 0 0 1 1.5 1.5V17a1.5 1.5 0 0 0 3 0V8l-3-3", 1.9)}</>);
export const RocketFill = glyph("RocketFill", P("M12 1.5c3.2 1.8 5 5.3 5 9.4V15l2.5 2.2V21L15 18.8H9L4.5 21v-3.8L7 15v-4.1c0-4.1 1.8-7.6 5-9.4zm0 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM10.3 20h3.4L12 23z"));
export const UtensilsFill = glyph(
  "UtensilsFill",
  P("M4 2h1.6v6h1.1V2h1.6v6h1.1V2H11v7a3 3 0 0 1-2 2.8V22H6.7V11.8A3 3 0 0 1 4.7 9H4zm13.5 0c1.9 0 3 2.5 3 6.5v5h-2V22h-2.3V2.6c0-.4.6-.6 1.3-.6z"),
);
export const CoffeeFill = glyph("CoffeeFill", <>{P("M3 8h14v6a6 6 0 0 1-6 6H9a6 6 0 0 1-6-6zm14 1.5h1.5a3 3 0 0 1 0 6H17v-2h1.5a1 1 0 0 0 0-2H17z")}{L("M7 2.5v2.8M10.5 2.5v2.8M14 2.5v2.8", 1.9)}</>);
export const ScaleFill = glyph(
  "ScaleFill",
  <>{L("M12 3v17.5M7 20.5h10M4 7h16", 2.2)}{P("M1.5 14 4 7.5 6.5 14a2.5 2.5 0 0 1-5 0zM17.5 14 20 7.5l2.5 6.5a2.5 2.5 0 0 1-5 0z")}</>,
);
export const WandFill = glyph(
  "WandFill",
  <>{L("M4 20 14.5 9.5", 3)}{P("M18 2l.9 2.1L21 5l-2.1.9L18 8l-.9-2.1L15 5l2.1-.9zM10.5 2.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6zM20 11l.6 1.4 1.4.6-1.4.6L20 15l-.6-1.4L18 13l1.4-.6z")}</>,
);
export const PuzzleFill = glyph(
  "PuzzleFill",
  P("M10 2.5a2 2 0 0 1 2 2V6h4a1 1 0 0 1 1 1v4h1.5a2 2 0 1 1 0 4H17v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4h1.5a2 2 0 1 0 0-4H3V7a1 1 0 0 1 1-1h4V4.5a2 2 0 0 1 2-2z"),
);
export const PartyPopperFill = glyph(
  "PartyPopperFill",
  <>{P("M3 21l4.5-12.5 8 8z")}{L("M13 3.5l.5 2.5M19 9l2-.5M16.5 5.5 19 3M15 11c1.5-1 3-1.2 4.5-.5M11.5 7.5c.8-1.4.9-3 .2-4.5", 1.9)}</>,
);
export const PiggyBankFill = glyph(
  "PiggyBankFill",
  P("M11 5c3.2 0 6 1.6 7.2 4H20a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.3a7 7 0 0 1-2.2 2.6V20a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-1.1a8 8 0 0 1-2 0V20a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-3.4A6.7 6.7 0 0 1 4 11.5c0-1 .2-1.9.6-2.7L3 6.5l3.2.4A7.6 7.6 0 0 1 11 5zm4.5 4.2a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"),
);
export const BanknoteFill = glyph(
  "BanknoteFill",
  P("M3 5.5h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zM12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM5 11v2h2v-2zm12 0v2h2v-2z"),
);
export const HandCoinsFill = glyph(
  "HandCoinsFill",
  P("M15.5 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM2 13h3.5L9 11.5h4a1.5 1.5 0 0 1 0 3h-3v1h4.2l5-3a1.6 1.6 0 0 1 1.9 2.5L15.5 20H2z"),
);
export const IndianRupeeFill = glyph("IndianRupeeFill", L("M6 4h12M6 9h12M8.5 4c6 0 6 10 0 10H7l8.5 7", 2.6));
export const WalletFill = glyph(
  "WalletFill",
  P("M5 4h12.5A1.5 1.5 0 0 1 19 5.5V7H5a1 1 0 0 0 0 2h14.5A2.5 2.5 0 0 1 22 11.5v7a2.5 2.5 0 0 1-2.5 2.5H5A3 3 0 0 1 2 18V7a3 3 0 0 1 3-3zm11.6 9a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"),
);
export const FlaskFill = glyph("FlaskFill", P("M8.5 2h7v2H15v4.8l5.4 9.6A2.4 2.4 0 0 1 18.3 22H5.7a2.4 2.4 0 0 1-2.1-3.6L9 8.8V4h-.5zM7.8 14l-.8 1.5h10l-.8-1.5z"));
export const ThermometerFill = glyph("ThermometerFill", <>{P("M12 2a3 3 0 0 1 3 3v8.8a5 5 0 1 1-6 0V5a3 3 0 0 1 3-3z")}{L("M17.5 5h2M17.5 8.5h2", 1.7)}</>);
export const DropletsFill = glyph("DropletsFill", P("M8 3s5 5.2 5 9a5 5 0 0 1-10 0c0-3.8 5-9 5-9zM17.5 11s3.5 3.6 3.5 6.2a3.5 3.5 0 0 1-7 0c0-2.6 3.5-6.2 3.5-6.2z"));
export const BugFill = glyph(
  "BugFill",
  <>{P("M9 3.5l1.3 1.8a4 4 0 0 1 3.4 0L15 3.5l1.3.9-1.2 1.8A4 4 0 0 1 16 8H8a4 4 0 0 1 .9-1.8L7.7 4.4zM7 9.5h10V15a5 5 0 0 1-10 0z")}{L("M3 11.5h3.5M17.5 11.5H21M3.5 17H7M17 17h3.5M4 6.5l3 3M20 6.5l-3 3", 1.9)}</>,
);

// ── Rewards & emphasis ───────────────────────────────────────────────────
export const StarFill = glyph("StarFill", P("M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5-4.8-4.6 6.6-.9z"));
export const HeartFill = glyph("HeartFill", P(HEART));
export const HeartPulseFill = knock("HeartPulseFill", HEART, C("M3 12h4.5l1.5-3 3 6 2-4 1 1h6", 1.9));
export const ThumbsUpFill = glyph("ThumbsUpFill", P("M2 10h4v11H2zM8 10l4-8a2.5 2.5 0 0 1 2.5 2.8L14 9h5.5a2 2 0 0 1 2 2.4l-1.6 8A2 2 0 0 1 18 21H8z"));
export const CrownFill = glyph("CrownFill", P("M2.5 7l4.8 4.2L12 4l4.7 7.2L21.5 7l-1.8 11H4.3zM4.3 19.5h15.4V21H4.3z"));
export const TrophyFill = glyph(
  "TrophyFill",
  <>
    {P("M6 2.5h12V4h3v2.5a4.5 4.5 0 0 1-4.1 4.5A6 6 0 0 1 13 14.4V17h3v2H8v-2h3v-2.6A6 6 0 0 1 7.1 11 4.5 4.5 0 0 1 3 6.5V4h3zM4.8 5.8v.7c0 1 .5 1.9 1.4 2.4-.1-.5-.2-.9-.2-1.4V5.8zm13.2 0v1.7c0 .5-.1.9-.2 1.4.9-.5 1.4-1.4 1.4-2.4v-.7z")}
    {P("M6.5 20h11v2h-11z")}
  </>,
);
export const MedalFill = glyph(
  "MedalFill",
  P("M6 2h4l2.3 5.2-3.3.9zm8 0h4l-3 6.1-3.3-.9zM12 8.5a6.75 6.75 0 1 1 0 13.5 6.75 6.75 0 0 1 0-13.5zm0 3.2-1 2.2-2.4.3 1.8 1.6-.5 2.4 2.1-1.2 2.1 1.2-.5-2.4 1.8-1.6-2.4-.3z"),
);
export const FlagFill = glyph("FlagFill", P("M4 2h2v1h13l-2.5 5L19 13H6v9H4z"));
export const FlameFill = glyph("FlameFill", P("M12 2c.5 3 3.2 4.6 4.6 7A7.5 7.5 0 0 1 12 22a7.5 7.5 0 0 1-7.5-7.5c0-2.8 1.5-4.8 3-6.2.2 1.8 1 3 2.3 3.6C9.2 8.4 10.2 4.6 12 2z"));
export const ZapFill = glyph("ZapFill", P("M13.5 2 4 13.5h6.5L9.5 22 20 9.5h-6.5z"));
export const GradCapFill = glyph("GradCapFill", P("M12 3 1 8.5 12 14l9-4.5V16h2V8.5zM5.5 12.6V17c0 1.8 3 3.5 6.5 3.5s6.5-1.7 6.5-3.5v-4.4L12 16z"));
export const BrainFill = knock(
  "BrainFill",
  "M9 2.5a3 3 0 0 1 3 1.2 3 3 0 0 1 3-1.2 3.2 3.2 0 0 1 3.4 3.2 3.5 3.5 0 0 1 2.5 5.1 3.5 3.5 0 0 1-1.4 5.6A3.4 3.4 0 0 1 15 21a3.2 3.2 0 0 1-3-1.4A3.2 3.2 0 0 1 9 21a3.4 3.4 0 0 1-4.5-4.6 3.5 3.5 0 0 1-1.4-5.6 3.5 3.5 0 0 1 2.5-5.1A3.2 3.2 0 0 1 9 2.5z",
  C("M12 4.5v15M8.5 9.5a2.5 2.5 0 0 0 3.5 0M15.5 13.5a2.5 2.5 0 0 0-3.5 0", 1.5),
);

// ── Weather ──────────────────────────────────────────────────────────────
export const CloudFill = glyph("CloudFill", P(CLOUD));
export const CloudOffFill = knock("CloudOffFill", CLOUD, slashCut, slashLine);
export const UploadCloudFill = knock("UploadCloudFill", CLOUD, C("M12 17v-6M9 13.5l3-3 3 3", 2.2));
export const CloudSunFill = glyph(
  "CloudSunFill",
  <>{Dot(8, 8.5, 3.6)}{L("M8 1.8v1.3M1.8 8.5h1.3M3.5 4l.9.9M12.5 4l-.9.9", 1.8)}{P("M9 21a4.3 4.3 0 0 1-.7-8.5 5.4 5.4 0 0 1 10.3-.6 4.1 4.1 0 0 1-.1 9.1z")}</>,
);
export const CloudRainFill = glyph("CloudRainFill", <>{P(CLOUD_HIGH)}{L("M8.5 18l-1 3M12.5 18l-1 3M16.5 18l-1 3", 2)}</>);
export const CloudDrizzleFill = glyph("CloudDrizzleFill", <>{P(CLOUD_HIGH)}{L("M8 18v.4M8 21.5v.1M12 19.5v.4M16 18v.4M16 21.5v.1", 2.2)}</>);
export const CloudSnowFill = glyph("CloudSnowFill", <>{P(CLOUD_HIGH)}{L("M8 18.5v.1M12 20.5v.1M16 18.5v.1M10 22.2v.1M14 22.2v.1", 2.6)}</>);
export const CloudLightningFill = glyph("CloudLightningFill", <>{P(CLOUD_HIGH)}{P("M12.5 13l-4 5.5h3l-1 4.5 4.5-6h-3l1.5-4z")}</>);
export const CloudFogFill = glyph("CloudFogFill", <>{P(CLOUD_HIGH)}{L("M4 18.5h16M7 21.5h10", 2)}</>);
export const CloudMoonFill = glyph(
  "CloudMoonFill",
  <>{P("M13.5 2.5a5.5 5.5 0 0 0 8 6.8 6.2 6.2 0 1 1-8-6.8z")}{P("M6.5 21a4 4 0 0 1-.6-8 5 5 0 0 1 9.5-.5 3.8 3.8 0 0 1-.4 8.5z")}</>,
);
export const MoonFill = glyph("MoonFill", P("M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11z"));
export const SnowflakeFill = glyph("SnowflakeFill", L("M12 2.5v19M3.8 7.2l16.4 9.6M3.8 16.8l16.4-9.6M9.5 3.8 12 6.3l2.5-2.5M9.5 20.2 12 17.7l2.5 2.5", 2.2));
export const WindFill = glyph("WindFill", L("M3 8h10.5a2.5 2.5 0 1 0-2.5-2.5M3 12h15.5a2.5 2.5 0 1 1-2.5 2.5M3 16h7.5a2.5 2.5 0 1 1-2.5 2.5", 2.3));

// ── Transport controls ───────────────────────────────────────────────────────
// Added for the product tour's player (components/tour). Nothing in either
// solid set covered "play" or "pause", and the house rule is that a missing
// glyph is drawn here rather than reached for in lucide.
export const PlayFill = glyph("PlayFill", P("M8.4 4.8a1.4 1.4 0 0 1 2.12-1.2l8.2 6.4a1.4 1.4 0 0 1 0 2.4l-8.2 6.4A1.4 1.4 0 0 1 8.4 17.6V4.8Z"));
export const PauseFill = glyph("PauseFill", P("M7 4.4h2.9a1 1 0 0 1 1 1v13.2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5.4a1 1 0 0 1 1-1Zm7.1 0H17a1 1 0 0 1 1 1v13.2a1 1 0 0 1-1 1h-2.9a1 1 0 0 1-1-1V5.4a1 1 0 0 1 1-1Z"));

// ── Giving (Donations & Fundraising, 2026-09-30) ─────────────────────────────
// A gift for in-kind donations and the "Support school" surfaces, and a loop
// for recurring giving (RefreshFill reads as "reload", which a monthly plan is
// not).
export const GiftFill = glyph(
  "GiftFill",
  P("M8.2 2.5c1.6 0 2.9 1 3.8 2.6.9-1.6 2.2-2.6 3.8-2.6a2.8 2.8 0 0 1 2.2 4.5H21a1 1 0 0 1 1 1V11H13V7h-2v4H2V8a1 1 0 0 1 1-1h3a2.8 2.8 0 0 1 2.2-4.5zm0 2a.8.8 0 1 0 0 2h2.3c-.6-1.3-1.4-2-2.3-2zm7.6 0c-.9 0-1.7.7-2.3 2h2.3a.8.8 0 0 0 0-2zM3 12.5h8V22H4a1 1 0 0 1-1-1zm10 0h8V21a1 1 0 0 1-1 1h-7z"),
);
export const RepeatFill = glyph("RepeatFill", L("M17 2.5 20.5 6 17 9.5M4 11V9.5A3.5 3.5 0 0 1 7.5 6h13M7 21.5 3.5 18 7 14.5M20 13v1.5a3.5 3.5 0 0 1-3.5 3.5h-13", 2.4));

// ── Share-row brand marks (simplified, single colour) ────────────────────────
// Drawn for ShareRow in components/donations — a share button is recognised by
// its mark faster than by its label. Every use also carries a visible or
// accessible text name, so the glyph is never the only cue.
export const WhatsAppFill = glyph(
  "WhatsAppFill",
  P("M12 2a9.8 9.8 0 0 0-8.5 14.7L2 22l5.4-1.4A9.8 9.8 0 1 0 12 2zm-3.3 5.3c.2 0 .5 0 .7.5l1 2.3c.1.2.1.5-.1.7l-.6.8c-.2.2-.2.4 0 .7a8 8 0 0 0 3.9 3.4c.3.1.5.1.7-.1l.8-1c.2-.3.5-.3.8-.2l2.2 1c.3.2.5.3.5.6a2.6 2.6 0 0 1-2 2.4c-1 .2-2.4.1-4.6-.9A10.6 10.6 0 0 1 7.2 12c-.8-1.2-1.3-2.6-.8-3.8.3-.6.8-.9 1.3-.9z"),
);
export const FacebookFill = glyph(
  "FacebookFill",
  P("M5 2h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3h-4.5v-7h2.6l.4-3h-3V10c0-.9.3-1.5 1.5-1.5h1.6V5.8a21 21 0 0 0-2.3-.1c-2.3 0-3.8 1.4-3.8 3.9V12H9v3h2.5v7H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3z"),
);
export const InstagramFill = glyph(
  "InstagramFill",
  P("M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9a5.5 5.5 0 0 1-5.5 5.5h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm5.3-3.6a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6z"),
);
export const LinkedInFill = glyph(
  "LinkedInFill",
  P("M5 2h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3zm1.7 7.5V18h2.8V9.5zM8.1 5a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2zm3.4 4.5V18h2.8v-4.4c0-1.2.4-2 1.5-2s1.4.9 1.4 2V18H20v-5.1c0-2.4-1.3-3.6-3.2-3.6-1.3 0-2.1.6-2.5 1.3V9.5z"),
);
export const XLogoFill = glyph("XLogoFill", P("M3 3h5.3l4.3 5.9L17.7 3h2.6l-6.5 7.5L21.3 21H16l-4.6-6.3L6 21H3.3l7-8z"));
